'use client';

import type { ResultOf } from '@graphql-typed-document-node/core';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { AccountMultiselectOption as AccountType } from 'src/features/tasks/creation/components/account-multiselect/account-multiselect-types';
import type { FragmentType } from 'src/gql';
import Link from 'next/link';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@savvy-kit/components/hover-card';
import { Separator } from '@savvy-kit/components/separator';
import { SortableHeaderContent } from '@savvy-kit/components/table';
import { isNil } from 'es-toolkit';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { DateTime } from 'luxon';
import { graphql } from 'src/gql';
import { padBulletsToMaskedAccountNumber } from 'src/utils/account';
import { formatDollarAmount, formatPercentage } from 'src/utils/formatting';

import type { ColumnDef } from '@savvy/data-table';
import { NumberCellContent } from '@savvy/data-table';

import type { AccountsTableColumn } from './utils/accounts-columns';
import type { SortBy, SortByOrder } from './utils/accounts-url-state';
import { AccountCustomNotePopover } from './account-custom-note-popover';
import { EnrollStrategyButton, StrategyEnrollmentChip } from './strategy-enrollment-chip';

// Maps each data-table column id to the AccountsTableColumn name used by the
// shared Display menu, so visibility can be filtered against the selection.
export const DATA_TABLE_COLUMN_ID_TO_NAME: Record<string, AccountsTableColumn> = {
  accountName: 'Account Name',
  balance: 'Balance',
  household: 'Household',
  primaryAdvisor: 'Primary Advisor',
  strategy: 'Strategy',
  cashAndMoneyMarket: 'Cash & Money Market',
  orionFeeSchedule: 'Orion Fee Schedule',
  orionFrequency: 'Orion Frequency',
  billStyle: 'Bill Style',
  orionPayMethod: 'Orion Pay Method',
  startDate: 'Start Date',
  recurringAdjustmentStartDate: 'Recurring Adjustment Start Date',
  recurringAdjustmentEndDate: 'Recurring Adjustment End Date',
  recurringAdjustmentSchedule: 'Recurring Adjustment Schedule',
  feeType: 'Fee Type',
  amountType: 'Amount Type',
  flatFeeAmount: 'Flat Fee Amount',
  minimumFee: 'Minimum Fee',
};

export const DATA_TABLE_NAME_TO_COLUMN_ID: Partial<Record<AccountsTableColumn, string>> = Object.fromEntries(
  Object.entries(DATA_TABLE_COLUMN_ID_TO_NAME).map(([columnId, columnName]) => [columnName, columnId]),
);

export const AccountsDataTableRowFragment = graphql(/* GraphQL */ `
  fragment AccountsDataTableRow on Account {
    id
    displayName
    accountNumber
    mask
    displayBalance {
      amount
    }
    cashAndMoneyMarket @include(if: $showCashMm) {
      amount
      percent
      currencyCode
    }
    lastUpdatedAt
    strategyName
    strategyId
    customNote
    orionFeeSchedule
    orionBillFrequency
    orionBillStyle
    orionBillPayMethod
    orionAccountStartDate
    recurringAdjustmentBillStartDate
    recurringAdjustmentBillEndDate
    recurringAdjustmentBillScheduleName
    activeFeeSchedule {
      feeType
      amountType
      flatFeeAmount
      minimumFee
    }
    portfolio {
      household {
        id
        name
        salesforceAccountId
        advisor {
          id
          user {
            id
            firstName
            lastName
          }
        }
      }
    }
    ...StrategyEnrollmentChip
  }
`);

export type AccountsDataTableRow = FragmentType<typeof AccountsDataTableRowFragment>;

export type AccountsDataTableRowUnmasked = ResultOf<typeof AccountsDataTableRowFragment>;

function StrategyBillingHoverCard({
  account,
  children,
}: {
  readonly account: AccountsDataTableRowUnmasked;
  readonly children: React.ReactNode;
}) {
  const hasBillingDetails =
    !isNil(account.orionFeeSchedule) ||
    !isNil(account.orionBillFrequency) ||
    !isNil(account.orionBillStyle) ||
    !isNil(account.orionBillPayMethod);

  if (!hasBillingDetails) {
    return children;
  }

  return (
    <HoverCard>
      <HoverCardTrigger render={<div className="cursor-default" />}>{children}</HoverCardTrigger>
      <HoverCardContent className="w-72" align="start">
        <div className="flex flex-col gap-3">
          <p className="text-sm font-semibold text-dark">Billing details</p>
          <Separator />
          <div className="flex flex-col gap-2">
            {!isNil(account.orionFeeSchedule) && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary">Fee schedule</span>
                <span className="text-xs text-dark">{account.orionFeeSchedule}</span>
              </div>
            )}
            {!isNil(account.orionBillFrequency) && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary">Frequency</span>
                <span className="text-xs text-dark">{account.orionBillFrequency}</span>
              </div>
            )}
            {!isNil(account.orionBillStyle) && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary">Bill style</span>
                <span className="text-xs text-dark">{account.orionBillStyle}</span>
              </div>
            )}
            {!isNil(account.orionBillPayMethod) && (
              <div className="flex items-center justify-between">
                <span className="text-xs text-secondary">Pay method</span>
                <span className="text-xs text-dark">{account.orionBillPayMethod}</span>
              </div>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

type ColumnContext = {
  onStartEnhancedEnrollment: (account: AccountType) => void;
  sortBy: SortBy | null;
  sortByOrder: SortByOrder | null;
  onCycleCashMmSort: () => void;
  setSortBy: (sortBy: SortBy) => void;
};

export function createAccountsDataTableColumns(context: ColumnContext): ColumnDef<AccountsDataTableRowUnmasked>[] {
  const { onStartEnhancedEnrollment, sortBy, sortByOrder, onCycleCashMmSort, setSortBy } = context;

  const isCashAmountSort = sortBy === 'cashAndMoneyMarketAmount';
  const isCashPercentSort = sortBy === 'cashAndMoneyMarketPercent';
  const cashMmActive = isCashAmountSort || isCashPercentSort;
  const CashMmArrow = (() => {
    if (cashMmActive) {
      if (sortByOrder === 'asc') {
        return ArrowUp;
      }

      return ArrowDown;
    }

    return null;
  })();
  const cashMmBadge = isCashPercentSort ? '%' : '$';

  const baseColumns: ColumnDef<AccountsDataTableRowUnmasked>[] = [
    {
      id: 'accountName',
      accessorKey: 'displayName',
      meta: { width: '30%' },
      header: () => (
        <SortableHeaderContent
          isSorted={sortBy === 'displayName'}
          sortDirection={sortByOrder === 'asc' ? 'ASC' : 'DESC'}
          onClick={() => setSortBy('displayName')}
        >
          Account Name
        </SortableHeaderContent>
      ),
      cell: ({ row }) => {
        const account = row.original;
        return (
          <div className="flex flex-col gap-0.5 group/account-row">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-dark">{account.displayName}</span>
              <AccountCustomNotePopover accountId={account.id} customNote={account.customNote} />
            </div>
            {!isNil(account.mask) && (
              <span className="text-sm text-secondary">{padBulletsToMaskedAccountNumber(account.mask)}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'balance',
      accessorFn: (row) => Number(row.displayBalance.amount),
      meta: { width: '15%' },
      header: () => (
        <SortableHeaderContent
          isSorted={sortBy === 'displayBalance'}
          sortDirection={sortByOrder === 'asc' ? 'ASC' : 'DESC'}
          onClick={() => setSortBy('displayBalance')}
          className="justify-end"
        >
          Balance
        </SortableHeaderContent>
      ),
      cell: ({ row }) => {
        const account = row.original;
        return (
          <NumberCellContent
            value={formatDollarAmount(account.displayBalance.amount, true)}
            description={DateTime.fromISO(account.lastUpdatedAt).toRelative() ?? undefined}
          />
        );
      },
    },
    {
      id: 'household',
      accessorFn: (row) => row.portfolio?.household.name,
      meta: { width: '22%' },
      header: () => (
        <SortableHeaderContent
          isSorted={sortBy === 'household'}
          sortDirection={sortByOrder === 'asc' ? 'ASC' : 'DESC'}
          onClick={() => setSortBy('household')}
        >
          Household
        </SortableHeaderContent>
      ),
      cell: ({ row }) => {
        const householdName = row.original.portfolio?.household.name;
        const salesforceAccountId = row.original.portfolio?.household.salesforceAccountId;

        if (isNil(householdName)) {
          return <span className="text-sm text-secondary">—</span>;
        }

        if (!isNil(salesforceAccountId)) {
          return (
            <Link
              href={`/advisor/dashboard/client/${salesforceAccountId}`}
              className="text-sm text-dark hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {householdName}
            </Link>
          );
        }

        return <span className="text-sm text-dark">{householdName}</span>;
      },
    },
    {
      id: 'primaryAdvisor',
      accessorFn: (row) => row.portfolio?.household.advisor?.user.id,
      meta: { width: '10%' },
      header: DATA_TABLE_COLUMN_ID_TO_NAME['primaryAdvisor'],
      cell: ({ row }) => {
        const advisorUser = row.original.portfolio?.household.advisor?.user;

        if (isNil(advisorUser)) {
          return <span className="text-sm text-secondary">—</span>;
        }

        const advisorName = `${advisorUser.firstName} ${advisorUser.lastName}`.trim();

        return <span className="text-sm text-dark">{advisorName}</span>;
      },
    },
    {
      id: 'strategy',
      accessorKey: 'strategyName',
      meta: { width: '20%' },
      header: 'Strategy',
      cell: ({ row }) => {
        const account = row.original;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <StrategyBillingHoverCard account={account}>
              <StrategyEnrollmentChip accountFrag={account} />
            </StrategyBillingHoverCard>
          </div>
        );
      },
    },
    {
      id: 'cashAndMoneyMarket',
      accessorFn: (row) => row.cashAndMoneyMarket,
      meta: { width: '18%' },
      header: () => {
        const handleKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCycleCashMmSort();
          }
        };
        const ariaSort = (() => {
          if (cashMmActive) {
            if (sortByOrder === 'asc') {
              return 'ascending';
            }

            return 'descending';
          }

          return 'none';
        })();
        return (
          <button
            type="button"
            onClick={onCycleCashMmSort}
            onKeyDown={handleKeyDown}
            aria-sort={ariaSort}
            className="flex items-center justify-end gap-1 bg-transparent p-0 w-full font-inherit cursor-pointer uppercase"
          >
            <span>Cash &amp; Money Market</span>
            <span aria-hidden="true" className="font-medium">
              {cashMmBadge}
              {!isNil(CashMmArrow) && <CashMmArrow aria-hidden="true" className="size-3.5 inline" />}
            </span>
          </button>
        );
      },
      cell: ({ row }) => {
        const value = row.original.cashAndMoneyMarket;
        if (isNil(value)) {
          return <NumberCellContent value="—" />;
        }
        return (
          <NumberCellContent
            value={formatDollarAmount(value.amount, true)}
            description={formatPercentage(value.percent, 1)}
          />
        );
      },
    },
  ];

  const orionColumns: ColumnDef<AccountsDataTableRowUnmasked>[] = [
    {
      id: 'orionFeeSchedule',
      accessorKey: 'orionFeeSchedule',
      meta: { width: '18%' },
      header: 'Orion Fee Schedule',
      cell: ({ row }) => <span className="text-sm text-dark break-words">{row.original.orionFeeSchedule ?? '—'}</span>,
    },
    {
      id: 'orionFrequency',
      accessorKey: 'orionBillFrequency',
      meta: { width: '16%' },
      header: 'Orion Frequency',
      cell: ({ row }) => <span className="text-sm text-dark">{row.original.orionBillFrequency ?? '—'}</span>,
    },
    {
      id: 'billStyle',
      accessorKey: 'orionBillStyle',
      meta: { width: '14%' },
      header: 'Bill Style',
      cell: ({ row }) => <span className="text-sm text-dark">{row.original.orionBillStyle ?? '—'}</span>,
    },
    {
      id: 'orionPayMethod',
      accessorKey: 'orionBillPayMethod',
      meta: { width: '16%' },
      header: 'Orion Pay Method',
      cell: ({ row }) => <span className="text-sm text-dark">{row.original.orionBillPayMethod ?? '—'}</span>,
    },
    {
      id: 'startDate',
      accessorKey: 'orionAccountStartDate',
      meta: { width: '13%' },
      header: () => (
        <SortableHeaderContent
          isSorted={sortBy === 'startDate'}
          sortDirection={sortByOrder === 'asc' ? 'ASC' : 'DESC'}
          onClick={() => setSortBy('startDate')}
        >
          Start Date
        </SortableHeaderContent>
      ),
      cell: ({ row }) => {
        const date = row.original.orionAccountStartDate;
        return (
          <span className="text-sm text-dark">
            {!isNil(date) ? DateTime.fromISO(date).toFormat('MMM d, yyyy') : '—'}
          </span>
        );
      },
    },
    {
      id: 'recurringAdjustmentStartDate',
      accessorKey: 'recurringAdjustmentBillStartDate',
      meta: { width: '22%' },
      header: 'Recurring Adjustment Start Date',
      cell: ({ row }) => {
        const date = row.original.recurringAdjustmentBillStartDate;
        return (
          <span className="text-sm text-dark">
            {!isNil(date) ? DateTime.fromISO(date).toFormat('MMM d, yyyy') : '—'}
          </span>
        );
      },
    },
    {
      id: 'recurringAdjustmentEndDate',
      accessorKey: 'recurringAdjustmentBillEndDate',
      meta: { width: '21%' },
      header: 'Recurring Adjustment End Date',
      cell: ({ row }) => {
        const date = row.original.recurringAdjustmentBillEndDate;
        return (
          <span className="text-sm text-dark">
            {!isNil(date) ? DateTime.fromISO(date).toFormat('MMM d, yyyy') : '—'}
          </span>
        );
      },
    },
    {
      id: 'recurringAdjustmentSchedule',
      accessorKey: 'recurringAdjustmentBillScheduleName',
      meta: { width: '21%' },
      header: 'Recurring Adjustment Schedule',
      cell: ({ row }) => (
        <span className="text-sm text-dark break-words">{row.original.recurringAdjustmentBillScheduleName ?? '—'}</span>
      ),
    },
  ];

  const feeScheduleColumns: ColumnDef<AccountsDataTableRowUnmasked>[] = [
    {
      id: 'feeType',
      accessorFn: (row) => row.activeFeeSchedule?.feeType,
      meta: { width: '12%' },
      header: 'Fee Type',
      cell: ({ row }) => <span className="text-sm text-dark">{row.original.activeFeeSchedule?.feeType ?? '—'}</span>,
    },
    {
      id: 'amountType',
      accessorFn: (row) => row.activeFeeSchedule?.amountType,
      meta: { width: '13%' },
      header: 'Amount Type',
      cell: ({ row }) => <span className="text-sm text-dark">{row.original.activeFeeSchedule?.amountType ?? '—'}</span>,
    },
    {
      id: 'flatFeeAmount',
      accessorFn: (row) => row.activeFeeSchedule?.flatFeeAmount,
      meta: { width: '14%' },
      header: 'Flat Fee Amount',
      cell: ({ row }) => (
        <span className="text-sm text-dark">{row.original.activeFeeSchedule?.flatFeeAmount ?? '—'}</span>
      ),
    },
    {
      id: 'minimumFee',
      accessorFn: (row) => row.activeFeeSchedule?.minimumFee,
      meta: { width: '13%' },
      header: 'Minimum Fee',
      cell: ({ row }) => <span className="text-sm text-dark">{row.original.activeFeeSchedule?.minimumFee ?? '—'}</span>,
    },
  ];

  const actionsColumn: ColumnDef<AccountsDataTableRowUnmasked> = {
    id: 'actions',
    enableSorting: false,
    meta: { width: '1%' },
    header: '',
    cell: ({ row }) => {
      const account = row.original;
      return (
        <div className="flex justify-end whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          <EnrollStrategyButton accountFrag={account} onStartEnhancedEnrollment={onStartEnhancedEnrollment} />
        </div>
      );
    },
  };

  return [...baseColumns, ...orionColumns, ...feeScheduleColumns, actionsColumn];
}
